import { Card } from "@/components/ui/card";

const TermsOfUse = () => {
  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <Card className="p-8">
          <h1 className="text-3xl font-bold mb-6">Terms of Use</h1>
          <p className="text-sm text-muted-foreground mb-6">
            Last updated: {new Date().toLocaleDateString()}
          </p>

          <div className="space-y-6">
            <section>
              <h2 className="text-2xl font-semibold mb-3">1. Agreement to Terms</h2>
              <p className="text-muted-foreground">
                By accessing and using NextWave AI's platform at app.nextwaveai.space ("Service"), you agree to be bound 
                by these Terms of Use ("Terms"). If you disagree with any part of these terms, you may not access the Service.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-3">2. Description of Service</h2>
              <p className="text-muted-foreground">
                NextWave AI provides an AI-powered analytics platform that allows businesses to upload data, 
                generate insights, create dashboards, and automate workflows. Our service includes but is not limited to:
              </p>
              <ul className="list-disc list-inside text-muted-foreground space-y-1 mt-2">
                <li>Data upload and processing capabilities</li>
                <li>AI-powered analytics and insights generation</li>
                <li>Custom dashboard creation and management</li>
                <li>CRM integrations (HubSpot, Zoho, etc.)</li>
                <li>Workflow automation and predictive modeling</li>
                <li>Team collaboration features</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-3">3. User Responsibilities</h2>
              <div className="space-y-3">
                <h3 className="text-lg font-medium">3.1 Account Security</h3>
                <ul className="list-disc list-inside text-muted-foreground space-y-1">
                  <li>Maintain the confidentiality of your account credentials</li>
                  <li>Notify us immediately of any unauthorized account access</li>
                  <li>Use strong, unique passwords for your account</li>
                  <li>Be responsible for all activities under your account</li>
                </ul>

                <h3 className="text-lg font-medium">3.2 Data Usage and Upload</h3>
                <ul className="list-disc list-inside text-muted-foreground space-y-1">
                  <li>Ensure you have legal rights to upload and process all data</li>
                  <li>Do not upload malicious, illegal, or harmful content</li>
                  <li>Respect intellectual property rights of third parties</li>
                  <li>Comply with applicable data protection laws (GDPR, CCPA, etc.)</li>
                  <li>Do not attempt to reverse engineer or compromise our AI systems</li>
                </ul>

                <h3 className="text-lg font-medium">3.3 System Usage</h3>
                <ul className="list-disc list-inside text-muted-foreground space-y-1">
                  <li>Use the service only for legitimate business purposes</li>
                  <li>Do not attempt to overwhelm or disrupt our systems</li>
                  <li>Respect usage limits and fair use policies</li>
                  <li>Do not share account access with unauthorized users</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-3">4. Prohibited Actions</h2>
              <p className="text-muted-foreground mb-2">You may not:</p>
              <ul className="list-disc list-inside text-muted-foreground space-y-1">
                <li>Violate any applicable laws or regulations</li>
                <li>Upload viruses, malware, or other harmful code</li>
                <li>Attempt to gain unauthorized access to our systems</li>
                <li>Use the service to compete with or create similar products</li>
                <li>Scrape, harvest, or collect user information</li>
                <li>Impersonate others or provide false information</li>
                <li>Use the service for spam or unsolicited communications</li>
                <li>Interfere with other users' access to the service</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-3">5. AI Output and Disclaimer</h2>
              <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-lg">
                <p className="text-muted-foreground">
                  <strong>Important:</strong> AI-generated insights, predictions, and recommendations are provided for informational 
                  purposes only. NextWave AI does not guarantee the accuracy, completeness, or reliability of AI outputs. 
                  You should always verify AI results and use your professional judgment before making business decisions.
                </p>
              </div>
              <ul className="list-disc list-inside text-muted-foreground space-y-1 mt-3">
                <li>AI outputs may contain errors or biases</li>
                <li>Results may vary based on data quality and context</li>
                <li>We are not liable for decisions made based on AI recommendations</li>
                <li>Always validate AI insights with domain experts</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-3">6. Intellectual Property</h2>
              <div className="space-y-3">
                <h3 className="text-lg font-medium">6.1 Our Rights</h3>
                <p className="text-muted-foreground">
                  NextWave AI and its trademarks, logos, and branding are our exclusive property. 
                  The platform's software, algorithms, and technology are protected by intellectual property laws.
                </p>

                <h3 className="text-lg font-medium">6.2 Your Rights</h3>
                <p className="text-muted-foreground">
                  You retain ownership of your uploaded data. By using our service, you grant us a limited license 
                  to process your data solely to provide the agreed services.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-3">7. Account Termination</h2>
              <div className="space-y-3">
                <h3 className="text-lg font-medium">7.1 Termination by You</h3>
                <p className="text-muted-foreground">
                  You may terminate your account at any time through account settings or by contacting support.
                </p>

                <h3 className="text-lg font-medium">7.2 Termination by Us</h3>
                <p className="text-muted-foreground">
                  We may suspend or terminate accounts that violate these Terms, engage in prohibited activities, 
                  or pose security risks. We will provide reasonable notice when possible.
                </p>

                <h3 className="text-lg font-medium">7.3 Effect of Termination</h3>
                <p className="text-muted-foreground">
                  Upon termination, your access will cease, and we may delete your data according to our retention policies. 
                  You can request data export before termination.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-3">8. Limitation of Liability</h2>
              <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg">
                <p className="text-muted-foreground">
                  TO THE MAXIMUM EXTENT PERMITTED BY LAW, NEXTWAVE AI SHALL NOT BE LIABLE FOR ANY INDIRECT, 
                  INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO LOSS OF 
                  PROFITS, DATA, OR USE, ARISING FROM YOUR USE OF THE SERVICE.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-3">9. Service Availability</h2>
              <p className="text-muted-foreground">
                While we strive for high availability, we do not guarantee uninterrupted service. 
                Maintenance, updates, and technical issues may cause temporary service disruptions.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-3">10. Privacy and Data Protection</h2>
              <p className="text-muted-foreground">
                Your privacy is important to us. Please review our Privacy Policy, which governs how we collect, 
                use, and protect your information. The Privacy Policy is incorporated into these Terms by reference.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-3">11. Changes to Terms</h2>
              <p className="text-muted-foreground">
                We may modify these Terms from time to time. Material changes will be communicated via email or 
                platform notification. Continued use of the service constitutes acceptance of updated Terms.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-3">12. Governing Law</h2>
              <p className="text-muted-foreground">
                These Terms are governed by applicable laws. Any disputes will be resolved through appropriate 
                legal channels in accordance with our jurisdiction.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-3">13. Contact Information</h2>
              <p className="text-muted-foreground">
                For questions about these Terms of Use, please contact us:
              </p>
              <div className="mt-3 space-y-1 text-muted-foreground">
                <p><strong>Email:</strong> support@nextwaveai.space</p>
                <p><strong>Legal:</strong> legal@nextwaveai.space</p>
                <p><strong>Website:</strong> https://nextwaveai.space</p>
              </div>
            </section>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default TermsOfUse;